import { requireAllBlockTypes, textInputBlocks } from '@/constants';
import { isNestedStateProp } from './index';

const rules = {
	required: value => {
		if (typeof value === 'boolean') {
			return value;
		}
		return String(value).trim().length > 0;
	},
	email: value =>
		/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
			value
		),
	phone: value => value.length > 9 && /^[\(\)\s\-\+\d]{10,17}$/.test(value),
	max: (value, max) => value.length <= max,
	min: (value, min) => value.length >= min
};

function isRequiredContact({ block = {}, datafieldId }) {
	return Object.keys(block.properties?.elements ?? {}).some(key => {
		const element = block.properties.elements[key];
		if (element.data_field === datafieldId) {
			return element.required;
		}
		if (!element.combine && element.subelements) {
			const isSubElement = Object.keys(element.subelements).some(subKey => {
				const subElement = element.subelements[subKey];
				return subElement.data_field === datafieldId;
			});

			if (isSubElement) {
				return element.required;
			}
		}
		return false;
	});
}

function isContactEmail({ datafieldId, block = {} }) {
	return block.properties?.elements?.email?.data_field === datafieldId;
}

function isContactPhone({ datafieldId, block = {} }) {
	return (
		block.properties?.elements?.phone?.data_field === datafieldId ||
		block.properties?.elements?.phone2 === datafieldId
	);
}

function getExtraInputElement(block = {}) {
	if (!block.properties?.elements_extra) {
		return null;
	}
	return block.properties.elements_extra[
		Object.keys(block.properties?.elements_extra).pop()
	];
}

const reasonToErrorMessageKeyMap = {
	required: 'required',
	requiredCheckbox: 'required',
	requiredContact: 'required',
	requiredMulti: 'required_multi',
	requiredExtra: 'required',
	email: 'invalid_email',
	businessEmail: 'invalid_business_email',
	phone: 'invalid_phone',
	min: 'too_short',
	max: 'too_long',
	minExtra: 'too_short',
	maxExtra: 'too_long',
	minOptions: 'min_options',
	maxOptions: 'max_options',
	recaptcha: 'required'
};

export function validate({ block = {}, state, datafieldId, allowedEmails = [] }) {
	const validators = {
		required() {
			if (
				!datafieldId &&
				block.properties?.required &&
				requireAllBlockTypes.indexOf(block.typeName) === -1
			) {
				return rules.required(state.value);
			}

			if (block.typeName === 'contact' && isRequiredContact({ block, datafieldId })) {
				return rules.required(state[datafieldId].value);
			}

			return true;
		},
		requiredCheckbox() {
			if (block.typeName === 'checkbox' && block.properties?.required) {
				return Object.keys(state)
					.filter(key => isNestedStateProp(key))
					.some(key => rules.required(state[key].value));
			}
			return true;
		},
		requiredContact() {
			if (block.typeName === 'contact' && isRequiredContact({ block, datafieldId })) {
				return rules.required(state[datafieldId].value);
			}
			return true;
		},
		requiredMulti() {
			if (
				block.properties?.required &&
				requireAllBlockTypes.indexOf(block.typeName) > -1
			) {
				const filtered = Object.keys(state).filter(key => isNestedStateProp(key));
				return filtered.every(key => rules.required(state[key].value));
			}
			return true;
		},
		requiredExtra() {
			const extraInputElement = getExtraInputElement(block);
			if (
				block.properties?.required &&
				extraInputElement &&
				state[extraInputElement.data_field].isVisible
			) {
				return rules.required(state[extraInputElement.data_field].value);
			}
			return true;
		},
		email() {
			if (block.typeName === 'email' || block.properties?.format === 'email') {
				return !state.value || rules.email(state.value);
			}

			if (block.typeName === 'contact' && isContactEmail({ block, datafieldId })) {
				return !state[datafieldId].value || rules.email(state[datafieldId].value);
			}
			return true;
		},
		businessEmail() {
			if (
				block.properties?.business_only ||
				(block.properties?.elements?.email?.business_only &&
					isContactEmail({ block, datafieldId }))
			) {
				const value = datafieldId ? state[datafieldId].value : state.value;

				return rules.email(value) && allowedEmails.indexOf(value) !== -1;
			}
			return true;
		},
		phone() {
			if (block.typeName === 'phone' || block.properties?.format === 'tel') {
				return !state.value || rules.phone(state.value);
			}

			if (block.typeName === 'contact' && isContactPhone({ block, datafieldId })) {
				return !state[datafieldId].value || rules.phone(state[datafieldId].value);
			}
			return true;
		},
		min() {
			if (
				block.properties?.boundary?.limitMin &&
				textInputBlocks.indexOf(block.typeName) > -1
			) {
				return rules.min(state.value, Number(block.properties?.boundary?.limitMin));
			}
			return true;
		},
		max() {
			if (
				block.properties?.boundary?.limitMax &&
				textInputBlocks.indexOf(block.typeName) > -1
			) {
				return rules.max(state.value, Number(block.properties?.boundary?.limitMax));
			}
			return true;
		},
		minExtra() {
			const extraInputElement = getExtraInputElement(block);
			if (
				block.properties?.boundary?.limitMin &&
				extraInputElement &&
				state[extraInputElement.data_field].isVisible
			) {
				return rules.min(
					state[extraInputElement.data_field].value,
					Number(block.properties?.boundary?.limitMin)
				);
			}
			return true;
		},
		maxExtra() {
			const extraInputElement = getExtraInputElement(block);
			if (
				block.properties?.boundary?.limitMax &&
				extraInputElement &&
				state[extraInputElement.data_field].isVisible
			) {
				return rules.max(
					state[extraInputElement.data_field].value,
					Number(block.properties?.boundary?.limitMax)
				);
			}
			return true;
		},
		minOptions() {
			if (block.properties?.minSelected) {
				const selected = Object.keys(state).filter(
					key =>
						isNestedStateProp(key) &&
						rules.required(state[key].value) &&
						typeof state[key].value === 'boolean' //boolean value means it's a checkbox, otherwise 'other' inputs will be taken into account
				);
				return selected.length >= block.properties.minSelected;
			}
			return true;
		},
		maxOptions() {
			if (block.properties?.maxSelected) {
				const selected = Object.keys(state).filter(
					key =>
						isNestedStateProp(key) &&
						rules.required(state[key].value) &&
						typeof state[key].value === 'boolean' //boolean value means it's a checkbox, otherwise 'other' inputs will be taken into account
				);
				return selected.length <= block.properties.maxSelected;
			}
			return true;
		},
		recaptcha() {
			if (state.id === 'recaptcha') {
				return rules.required(state.value);
			}
			return true;
		}
	};

	if (!state.isVisible) {
		return {
			valid: true
		};
	}

	let valid = true;
	let reason = null;

	for (const key in validators) {
		const validator = validators[key];
		if (!validator()) {
			valid = false;
			reason = reasonToErrorMessageKeyMap[key];
			break;
		}
	}

	return {
		valid,
		reason,
		block
	};
}
